// Form file attachments — shared by the public renderer, the submit routes and the dashboard.

import { collectNamedFormElements } from "./form-schema-walk";

export const FORM_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const FORM_ATTACHMENT_MAX_FILES = 5;
// Multipart part name prefix for a file field: `file:<fieldName>`.
export const FORM_ATTACHMENT_PART_PREFIX = "file:";
export const FORM_ATTACHMENT_BLOB_PREFIX = "form-attachments/";

const PDF = [0x25, 0x50, 0x44, 0x46];
const PNG = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const JPEG = [0xff, 0xd8, 0xff];
const ZIP = [0x50, 0x4b, 0x03, 0x04];

interface AllowedAttachmentType {
	mimeType: string;
	signature: number[];
}

// Extension → stored MIME + magic bytes. The browser's declared MIME is never trusted.
const ALLOWED_TYPES: Record<string, AllowedAttachmentType> = {
	pdf: { mimeType: "application/pdf", signature: PDF },
	jpg: { mimeType: "image/jpeg", signature: JPEG },
	jpeg: { mimeType: "image/jpeg", signature: JPEG },
	png: { mimeType: "image/png", signature: PNG },
	docx: {
		mimeType:
			"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
		signature: ZIP,
	},
	xlsx: {
		mimeType:
			"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		signature: ZIP,
	},
};

export const FORM_ATTACHMENT_EXTENSIONS = Object.keys(ALLOWED_TYPES);

// Value for `<input accept>`.
export const FORM_ATTACHMENT_ACCEPT = FORM_ATTACHMENT_EXTENSIONS.map(
	(ext) => `.${ext}`
).join(",");

export interface FormAttachmentDescriptor {
	name: string;
	size: number;
}

export function collectFileFieldNames(schemaRoot: unknown): string[] {
	return collectNamedFormElements(schemaRoot)
		.filter(
			(n) => n.type === "form_field" && n.fieldType === "file" && !n.hidden
		)
		.map((n) => n.name ?? "")
		.filter(Boolean);
}

export function fileExtension(name: string): string {
	const dot = name.lastIndexOf(".");
	return dot === -1 ? "" : name.slice(dot + 1).toLowerCase();
}

export function isAllowedAttachmentName(name: string): boolean {
	return fileExtension(name) in ALLOWED_TYPES;
}

export function attachmentMimeType(name: string): string | null {
	return ALLOWED_TYPES[fileExtension(name)]?.mimeType ?? null;
}

export function signatureMatchesExtension(
	bytes: Uint8Array,
	name: string
): boolean {
	const sig = ALLOWED_TYPES[fileExtension(name)]?.signature;
	if (!sig || bytes.length < sig.length) {
		return false;
	}
	return sig.every((b, i) => bytes[i] === b);
}

// Metadata only — never used in a blob path. Strips control chars and path separators.
export function sanitizeAttachmentName(name: string): string {
	let out = "";
	for (const ch of name) {
		const c = ch.codePointAt(0) ?? 0;
		if (c < 32 || c === 127) {
			continue;
		}
		out += ch === "/" || ch === "\\" ? "_" : ch;
	}
	const trimmed = out.trim();
	if (trimmed.length <= 200) {
		return trimmed || "file";
	}
	const ext = fileExtension(trimmed);
	return `${trimmed.slice(0, 190)}${ext ? `.${ext}` : ""}`;
}

export function formatAttachmentSize(bytes: number): string {
	if (bytes < 1024) {
		return `${bytes} B`;
	}
	if (bytes < 1024 * 1024) {
		return `${Math.round(bytes / 1024)} KB`;
	}
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Stored as the field's value so exports, emails and webhooks read it like any text answer.
export function formatAttachmentSummary(
	files: readonly FormAttachmentDescriptor[]
): string {
	return files
		.map((f) => `${f.name} (${formatAttachmentSize(f.size)})`)
		.join(", ");
}

export function isAttachmentDescriptor(
	v: unknown
): v is FormAttachmentDescriptor {
	return (
		typeof v === "object" &&
		v !== null &&
		typeof (v as FormAttachmentDescriptor).name === "string" &&
		typeof (v as FormAttachmentDescriptor).size === "number"
	);
}

export function maxFilesForField(max: number | undefined): number {
	if (typeof max !== "number" || !Number.isFinite(max) || max < 1) {
		return 1;
	}
	return Math.min(Math.floor(max), FORM_ATTACHMENT_MAX_FILES);
}

// Returns an error message, or null when the descriptors pass.
export function validateAttachmentDescriptors(
	value: unknown,
	required: boolean,
	maxFiles: number
): string | null {
	const files = Array.isArray(value)
		? value.filter(isAttachmentDescriptor)
		: [];
	if (Array.isArray(value) && files.length !== value.length) {
		return "Please choose a valid file.";
	}
	if (files.length === 0) {
		return required ? "Please attach a file." : null;
	}
	if (files.length > maxFiles) {
		return maxFiles === 1
			? "Please attach only one file."
			: `Please attach at most ${maxFiles} files.`;
	}
	for (const f of files) {
		if (!isAllowedAttachmentName(f.name)) {
			return `"${f.name}" is not an allowed file type (${FORM_ATTACHMENT_EXTENSIONS.join(", ")}).`;
		}
		if (f.size <= 0) {
			return `"${f.name}" is empty.`;
		}
		if (f.size > FORM_ATTACHMENT_MAX_BYTES) {
			return `"${f.name}" is larger than ${formatAttachmentSize(FORM_ATTACHMENT_MAX_BYTES)}.`;
		}
	}
	return null;
}
